import { createTheme } from '@mui/material/styles';
import IconCheked from '../assets/icons/IconChecked';
import IconUncheked from '../assets/icons/IconUncheked';
import IconChevDown from '../assets/icons/IconChevDown';
import IconDate from '../assets/icons/IconDate';

const customTypography = {
  fontFamily: '"DM Sans", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,
  fontWeightExtrabold: 800,
  fontWeightBlack: 900,
  h1: { fontSize: '54px', fontWeight: 800, lineHeight: '120%' },
  h2: { fontSize: '32px', fontWeight: 'bold' },
  h3: { fontSize: '28px', fontWeight: 600 },
  h4: { fontSize: '24px', fontWeight: 600 },
  h5: { fontSize: '20px', fontWeight: 600 },
  h6: { fontSize: '18px', fontWeight: 600 },
  subtitle1: { fontSize: '16px' },
  subtitle2: { fontSize: '12px' },
  body1: { fontSize: '16px' },
  body2: { fontSize: '14px' },
  formlabel: { fontSize: '14px', fontWeight: 600 },
};

const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        textTransform: 'none',
        padding: '8px 12px',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '24px',
        gap: '0',
        minWidth: '40px',
      },
      sizeSmall: {
        fontSize: '14px',
        padding: '6px 12px',
      },
      sizeMedium: {
        fontSize: '14px',
        padding: '8px 12px',
      },
      sizeLarge: {
        borderRadius: '10px',
        fontSize: '16px',
        padding: '10px 12px',
      },
      containedPrimary: {},
    },
    defaultProps: {
      disableRipple: false,
      disableElevation: true,
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        backgroundColor: theme.palette.common.white,
        borderRadius: '10px',
        borderColor: theme.palette.text.secondary,
        '& .MuiInputBase-input': {
          color: theme.palette.text.primary,
          fontSize: '14px',
          borderRadius: '10px',
          padding: '10px 12px',
          fontWeight: 500,
          ...(ownerState.size === 'large' && {
            fontSize: '16px',
            padding: '16px',
            borderRadius: '10px',
          }),
        },
        '& .MuiInputBase-input::placeholder': {
          fontWeight: 400,
          // opacity: 1,
          // color: theme.palette.text.secondary,
        },
        '& .MuiInputBase-root': {
          borderRadius: '10px',
        },
        '& .MuiInputLabel-root': {
          color: theme.palette.text.primary,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.secondary,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.secondary,
        },
        '&.Mui-focused.MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.secondary,
        },
        '& .MuiInputAdornment-positionStart': {
          marginRight: 0,
        },
        '& .MuiInputBase-inputAdornedStart': {
          paddingLeft: '8px',
        },
        '& .MuiInputBase-root.Mui-disabled': {
          backgroundColor: theme.palette.background.default,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
        },
        '& .MuiInputBase-input.Mui-disabled': {
          color: theme.palette.text.primary,
          WebkitTextFillColor: theme.palette.text.primary,
        },
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {},
    },
  },
  MuiSelect: {
    defaultProps: {
      autoFocus: false,
      IconComponent: IconChevDown,
      displayEmpty: true,
    },
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        backgroundColor: theme.palette.common.white,
        borderRadius: '10px',
        borderColor: theme.palette.text.secondary,
        fontSize: '14px',
        '& .MuiSelect-select': {
          color: ownerState.value === '' ? theme.palette.text.disabled : theme.palette.text.primary,
          fontWeight: ownerState.value === '' ? 400 : 500,
          borderRadius: '10px',
          padding: '10px 12px',
        },
        '& .MuiSelect-select.MuiSelect-placeholder': {
          fontWeight: 400,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.secondary,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
      }),
      icon: {
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      icon: <IconCheked width={16} height={16} />,
      checkedIcon: <IconUncheked width={16} height={16} />,
    },
    styleOverrides: {
      root: {},
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: 48,
        height: 24,
        padding: 0,
      }),
      switchBase: ({ theme }) => ({
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(24px)',
          color: theme.palette.common.white,
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            opacity: 1,
          },
        },
        '&.Mui-disabled': {
          opacity: 0.4,
        },
      }),
      thumb: {
        width: 20,
        height: 20,
        boxShadow: 'none',
      },
      track: ({ theme }) => ({
        borderRadius: 12,
        backgroundColor: theme.palette.grey[300],
        border: `1px solid ${theme.palette.grey[300]}`,
        opacity: 1,
        transition: 'background-color 0.3s, border-color 0.3s',
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: '12px',
        fontWeight: '500',
        '&.Mui-error': {
          color: theme.palette.error.main,
          borderColor: theme.palette.error.main,
        },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.10)',
        borderRadius: '16px',
      }),
      content: {},
      media: {},
    },
    variants: [
      {
        props: { className: 'card-secondary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          borderRadius: '8px',
          border: '1px solid #CFCDDA',
          boxShadow: '0 0 12px rgba(0,0,0,0.05)',
        }),
      },
      {
        props: { className: 'card-nogap' },
        style: ({ theme }) => ({}),
      },
    ],
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        '&.MuiCardContent-root:last-child': {
          paddingBottom: '16px',
        },
        '.card-secondary &': {
          padding: '20px',
        },
        '.card-secondary &:last-child': {
          paddingBottom: '20px',
        },
        '.card-nogap  > &': {
          padding: '0',
        },
        '.card-nogap  > &:last-child': {
          paddingBottom: '0',
        },
      },
    },
  },
  MuiList: {
    defaultProps: {
      autoFocus: false,
    },
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        padding: '0 12px',
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.light,
        color: theme.palette.secondary.main,
        flexDirection: 'column',
        padding: '12px',
        borderRadius: '6px',
        gap: '10px',
        width: '100%',
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '& .MuiListItemText-primary': {
            color: theme.palette.common.white,
            fontWeight: 600,
          },
          '& svg': {
            color: theme.palette.common.white,
          },
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
          },
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '& .MuiListItemText-primary': {
            color: theme.palette.common.white,
          },
          '& svg': {
            color: theme.palette.common.white,
          },
        },
      }),
    },
    variants: [
      {
        props: { className: 'list-button-secondary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          color: theme.palette.text.light,
          flexDirection: 'row',
          alignItems: 'center',
          padding: '12px 16px 12px 36px',
          minHeight: '48px',
          borderRadius: '0',
          gap: '10px',
          width: '100%',
          '& .MuiListItemText-root .MuiTypography-root': {
            // fontSize: "16px"
          },
          '&.Mui-selected': {
            backgroundColor: '#EDF2FF',
            color: theme.palette.primary.main,
            '& .MuiListItemText-primary': {
              color: theme.palette.primary.main,
              fontWeight: 700,
            },
            '&:hover': {
              backgroundColor: '#EDF2FF',
            },
          },
          '&:hover': {
            backgroundColor: '#EDF2FF',
            color: theme.palette.common.white,
            '& .MuiListItemText-primary': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiListItemText-primary': {
            fontSize: '14px',
            color: theme.palette.text.light,
            fontWeight: 500,
          },
          '& .MuiListItemIcon-root ': {
            marginTop: 0,
            // minWidth: 'inherit',
          },
        }),
      },
    ],
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 'inherit',
        color: theme.palette.secondary.main,
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        margin: 0,
      },
      primary: ({ theme }) => ({
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '100%',
        color: theme.palette.secondary.main,
        margin: 0,
      }),
    },
  },
  MuiMenu: {
    defaultProps: {
      autoFocus: false,
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: '12px',
        boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.12)',
        // border: '1px solid rgba(194,201,214, 0.4)',
        border: `1px solid ${theme.palette.divider}`,
        marginTop: '4px',
        padding: 0,
      }),
      list: {
        // paddingTop: "8px",
        // paddingBottom: "8px",
        padding: 0,
      },
    },
  },
  MuiMenuList: {
    styleOverrides: {
      root: ({ theme }) => ({}),
    },
  },
  MuiMenuItem: {
    defaultProps: {
      autoFocus: false,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: '14px',
        fontWeight: 500,
        padding: '8px 12px',
        borderRadius: 0,
        minHeight: 0,
        color: theme.palette.secondary.main,
        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        },
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 48,
        borderBottom: '1px solid #E7E6EA',
      },
      indicator: {
        height: 2.5,
        borderRadius: 3,
        backgroundColor: '#1E3A8A',
      },
      flexContainer: {
        gap: 0,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 48,
        minWidth: 100,
        fontWeight: 500,
        fontSize: '14px',
        color: theme.palette.text.light,
        textTransform: 'none',
        padding: '8px 12px',
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      }),
    },
  },
  MuiFab: {
    styleOverrides: {
      root: ({ theme }) => ({
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000,
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        [theme.breakpoints.down('sm')]: {
          right: '16px',
          bottom: '16px',
        },
      }),
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiBreadcrumbs-separator': {
          margin: '0 8px',
          [theme.breakpoints.down('md')]: {
            margin: '0 4px',
          },
        },
      }),
    },
  },
  // MuiDataGrid: {
  //   styleOverrides: {
  //     root: {
  //       backgroundColor: '#fff',
  //       border: '1px solid #E7E6EA',
  //       borderRadius: '8px',
  //     },
  //     columnHeaders: {
  //       backgroundColor: '#F5F5F5',
  //       borderBottom: '0',
  //     },
  //     columnHeader: {
  //       fontWeight: '600',
  //       fontSize: '14px',
  //       padding: '8px',
  //       '&.MuiDataGrid-columnHeader:focus': {
  //         outline: 0,
  //         outlineOffset: 0
  //       },
  //     },
  //     columnHeaderTitle: {
  //       fontWeight: 600,
  //       overflow: 'hidden',
  //       textOverflow: 'ellipsis',
  //       whiteSpace: 'nowrap',
  //     },
  //     row: {
  //       height: 'auto',
  //       '&.Mui-selected': {
  //         backgroundColor: '#ffffff',
  //       },
  //       '&.Mui-selected:hover': {
  //         backgroundColor: 'transparent',
  //       },
  //       '&:hover': {
  //         backgroundColor: 'transparent',
  //       },
  //       "&.child-row": {
  //         backgroundColor: '#f5f5f5',
  //       },
  //     },
  //     cell: {
  //       fontSize: '14px',
  //       padding: '8px',
  //       color: '#666666',
  //       '&.MuiDataGrid-cell': {
  //         display: 'flex',
  //         alignItems: 'center',
  //         lineHeight: '34px',
  //       },
  //       '&.MuiDataGrid-cell:focus': {
  //         outline: 0,
  //         outlineOffset: 0
  //       },
  //     },
  //     footerContainer: {
  //       backgroundColor: '#fff',
  //       borderTop: '1px solid #e0e0e0',
  //     },
  //     toolbarContainer: {

  //     },
  //     iconButton: {
  //     },
  //     columnSeparator: {
  //       display: 'none',
  //     },
  //     checkboxInput: {
  //     },
  //     overlay: {
  //     },
  //     virtualScroller: {
  //     },
  //     noRowsOverlay: {
  //       padding: '20px',
  //       fontSize: '14px',
  //       color: '#888',
  //     },
  //     noResultsOverlay: {
  //       padding: '20px',
  //       fontSize: '14px',
  //       color: '#888',
  //     },
  //     pinnedColumnHeaders: {
  //       backgroundColor: '#f0f0f0',
  //     },
  //     pinnedRows: {
  //       backgroundColor: '#f9f9f9',
  //     },
  //     columnHeaderCheckbox: {
  //       // paddingLeft: '8px',
  //     },
  //     cellCheckbox: {
  //       // paddingLeft: '8px',
  //     },
  //   },
  // },
  MuiChip: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        let backgroundColor = '';
        let textColor = '';

        const getTransparent = (color, opacity) =>
          `${theme.palette.mode === 'dark' ? '#fff' : color}${Math.round(opacity * 255)
            .toString(16)
            .padStart(2, '0')}`;

        switch (ownerState.color) {
          case 'primary':
            backgroundColor = getTransparent(theme.palette.primary.main, 0.1);
            textColor = theme.palette.primary.main;
            break;
          case 'secondary':
            backgroundColor = getTransparent(theme.palette.secondary.main, 0.1);
            textColor = theme.palette.secondary.main;
            break;
          case 'error':
            backgroundColor = getTransparent(theme.palette.error.main, 0.1);
            textColor = theme.palette.error.main;
            break;
          case 'warning':
            backgroundColor = getTransparent(theme.palette.warning.main, 0.1);
            textColor = theme.palette.warning.main;
            break;
          case 'success':
            backgroundColor = getTransparent(theme.palette.success.main, 0.1);
            textColor = theme.palette.success.main;
            break;
          default:
            backgroundColor = theme.palette.grey[200];
            textColor = theme.palette.text.primary;
        }

        return {
          backgroundColor,
          color: textColor,
          borderRadius: '32px',
          padding: '8px 4px',
          height: '28px',
          fontSize: '12px',
          fontWeight: 500,
        };
      },
      label: {
        fontSize: '12px',
        fontWeight: '500',
      },
      deleteIcon: {},
    },
  },
  MuiPagination: {
    styleOverrides: {
      root: ({ theme }) => ({}),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '8px',
        margin: '0 2px',
        backgroundColor: theme.palette.common.white,
        color: theme.palette.secondary.main,
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          },
        },
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        },
      }),
    },
  },
  MuiDrawer: {
    variants: [
      {
        props: { variant: 'modal-rightSide' },
        style: ({ theme }) => ({
          width: 400,
          right: 0,
          left: 'auto',
          border: 0,
          color: theme.palette.secondary.main,
          '& .MuiDrawer-paper': {
            width: 400,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.common.white,
            color: theme.palette.secondary.main,
            border: 0,
            right: 0,
            left: 'auto',

            '& .drawer-header': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          },
          '& .drawer-body': {
            flex: 1,
            overflow: 'auto',
          },
          '& .drawer-footer': {
            borderTop: '1px solid #C4C4C4',
            display: 'flex',
            justifyContent: 'end',
            gap: '12px',
          },
        }),
      },
    ],
  },
  MuiDatePicker: {
    defaultProps: {
      displayWeekNumber: false,
      slots: {
        openPickerIcon: IconDate,
      },
      slotProps: {
        textField: {
          size: 'small',
          variant: 'outlined',
          sx: {
            '& .MuiPickersInputBase-root': {
              padding: '0 12px',
              minHeight: '40px',
              fontSize: '14px',
              borderRadius: '8px',
            },
            '& .MuiPickersSectionList-root': {
              padding: '8px 0',
            },
            '& .MuiInputBase-input': {
              padding: '10px 12px',
              minHeight: 40,
              fontSize: 14,
            },
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: '#9e9e9e',
            },
            '&:hover .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: '#1976d2',
            },
          },
        },
        popper: {
          sx: {
            '& .MuiPaper-root': {
              boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            },
          },
        },
      },
    },
  },

  MuiDateRangeCalendar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(2),
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        color: theme.palette.text.primary,
      }),

      monthContainer: {
        padding: 8,
      },
      day: ({ theme }) => ({
        fontSize: 14,
        fontWeight: 500,
        padding: 6,
        borderRadius: 8,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        },
      }),
      weekDayLabel: ({ theme }) => ({
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
      }),
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 8,
        borderRadius: 8,
        backgroundColor: '#E2E8F0',
      },
      bar: {
        borderRadius: 4,
      },
    },
  },
};

const lightTheme = createTheme({
  cssVarPrefix: 'mui',
  enableColorScheme: true,
  palette: {
    mode: 'light',
    primary: {
      main: '#1E3A8A',
      dark: '#1e44af',
    },
    secondary: {
      main: '#1E293B',
    },
    error: {
      main: '#EB4335',
    },
    success: {
      main: '#00C16A',
    },
    warning: {
      main: '#F8B70D',
    },
    text: {
      primary: '#272128',
      secondary: '#90A1B9',
      light: '#666666',
    },
    background: {
      default: '#F3F2F4',
      light: '#f6f7f9',
    },
  },
  typography: customTypography,
  components,
});

const darkTheme = createTheme({
  cssVarPrefix: 'mui',
  enableColorScheme: true,
  palette: {
    mode: 'dark',
    primary: {
      main: '#1E3A8A',
      dark: '#1e44af',
    },
    secondary: {
      main: '#1E293B',
    },
    error: {
      main: '#EB4335',
    },
    success: {
      main: '#00C16A',
    },
    warning: {
      main: '#F8B70D',
    },
    text: {
      primary: '#272128',
      secondary: '#90A1B9',
      light: '#666666',
    },
    background: {
      default: '#F3F2F4',
      light: '#fbfbfb',
    },
  },
  typography: customTypography,
  components,
});

export { lightTheme, darkTheme };
